import React, { useState } from "react";
import { toMeter } from "../../../utils/unitConverter";
import ReadinessScore from "./ReadinessScore";
import CalculatorNotes from "./CalculatorNotes";
import QuickChecklist, { initialChecklist } from "./QuickChecklist";
import DownloadPDF from "./DownloadConcretePDF";
import CalculatorInquiryModal from "../Common/CalculatorInquiryModal";

const SlabCalculator = () => {
  const [form, setForm] = useState({
    length: "",
    lengthUnit: "m",
    width: "",
    widthUnit: "m",
    height: "",
    heightUnit: "cm",
    quantity: 1,
  });

  const [result, setResult] = useState(null);
  const [checklist, setChecklist] = useState(initialChecklist);
  const [showInquiryModal, setShowInquiryModal] = useState(false);
  const [allowDownload, setAllowDownload] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (
      ["length", "width", "height", "quantity"].includes(name) &&
      value !== "" &&
      !/^\d*\.?\d*$/.test(value)
    ) {
      return;
    }

    setForm({ ...form, [name]: value });
  };

  const calculate = () => {
    const l = toMeter(parseFloat(form.length), form.lengthUnit);
    const w = toMeter(parseFloat(form.width), form.widthUnit);
    const h = toMeter(parseFloat(form.height), form.heightUnit);
    const q = parseFloat(form.quantity);

    const volumeM3 = l * w * h * q;

    const volumeFt3 = volumeM3 * 35.3147;
    const volumeYd3 = volumeM3 * 1.30795;

    const weightKg = volumeM3 * 2130;
    const weightLbs = weightKg * 2.20462;

    const bags60 = weightLbs / 60;
    const bags80 = weightLbs / 80;

    setResult({
      volumeM3,
      volumeFt3,
      volumeYd3,
      weightKg,
      weightLbs,
      bags60,
      bags80,
    });
  };

  const clearForm = () => {
    setForm({
      length: "",
      lengthUnit: "m",
      width: "",
      widthUnit: "m",
      height: "",
      heightUnit: "cm",
      quantity: 1,
    });
    setResult(null);
  };

  const checkedCount = checklist.filter((item) => item.checked).length;

  const scoreMap = {
    0: 0,
    1: 20,
    2: 35,
    3: 45,
    4: 55,
    5: 65,
    6: 75,
    7: 82,
    8: 89,
    9: 95,
  };

  const readinessScore = scoreMap[checkedCount];

  const calculatorName = "Slab / Square Footings / Wall Calculator";

  return (
    <div className=" p-6 bg-white rounded-2xl shadow-xl border">
      <h2 className="text-xl font-semibold font-heading text-start text-gray-800 mb-4">
        {calculatorName}
      </h2>

      <div className="flex flex-col sm:flex-row gap-8 items-start">
        {/* LEFT FORM */}
        <div className="space-y-5 w-full sm:w-1/2">
          {[
            { label: "Length", name: "length", unit: "lengthUnit" },
            { label: "Width", name: "width", unit: "widthUnit" },
            {
              label: "Thickness or Height",
              name: "height",
              unit: "heightUnit",
            },
          ].map((field, index) => (
            <div key={index} className="space-y-1">
              <label className="text-gray-600 font-medium">{field.label}</label>

              <div className="flex gap-2">
                <input
                  type="text"
                  name={field.name}
                  value={form[field.name]}
                  onChange={handleChange}
                  placeholder={`Enter ${field.label}`}
                  className="w-full border px-3 py-2 rounded-lg focus:ring-2 focus:ring-btnColor outline-none"
                />

                <select
                  name={field.unit}
                  value={form[field.unit]}
                  onChange={handleChange}
                  className="border px-2 rounded-lg bg-gray-50"
                >
                  <option value="m">m</option>
                  <option value="cm">cm</option>
                  <option value="mm">mm</option>
                  <option value="ft">ft</option>
                  <option value="yd">yd</option>
                  <option value="in">in</option>
                </select>
              </div>
            </div>
          ))}

          {/* Quantity */}
          <div>
            <label className="text-gray-600 font-medium">Quantity</label>
            <input
              type="text"
              name="quantity"
              value={form.quantity}
              onChange={handleChange}
              className="w-full border px-3 py-2 rounded-lg focus:ring-2 focus:ring-btnColor outline-none mt-1"
            />
          </div>

          {/* Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              onClick={calculate}
              className="flex-1 bg-btnColor text-white py-3 rounded-xl shadow-md hover:bg-btnHoverColor border border-btnColor transition"
            >
              Action
            </button>

            <button
              onClick={clearForm}
              className="flex-1 bg-gray-200 py-3 rounded-xl hover:bg-gray-700 hover:text-white transition"
            >
              Clear
            </button>
          </div>
        </div>

        <div className="flex flex-col w-full sm:w-1/2 items-center sm:justify-between">
          {/* CENTER SVG DIAGRAM */}
          <div className="flex justify-center">
            <svg
              width="300"
              height="220"
              viewBox="0 0 300 220"
              className="drop-shadow-sm"
            >
              <defs>
                {/* Arrowheads */}
                <marker
                  id="arrowBlue"
                  markerWidth="6"
                  markerHeight="6"
                  refX="5"
                  refY="3"
                  orient="auto"
                >
                  <path d="M0,0 L6,3 L0,6 Z" fill="#2563eb" />
                </marker>

                <marker
                  id="arrowGreen"
                  markerWidth="6"
                  markerHeight="6"
                  refX="5"
                  refY="3"
                  orient="auto"
                >
                  <path d="M0,0 L6,3 L0,6 Z" fill="#16a34a" />
                </marker>

                <marker
                  id="arrowRed"
                  markerWidth="6"
                  markerHeight="6"
                  refX="5"
                  refY="3"
                  orient="auto"
                >
                  <path d="M0,0 L6,3 L0,6 Z" fill="#dc2626" />
                </marker>
              </defs>

              {/* Top Face */}
              <polygon
                points="60,70 200,70 250,110 110,110"
                fill="#f9fafb"
                stroke="#9ca3af"
                strokeWidth="1"
              />

              {/* Front Face */}
              <polygon
                points="110,110 250,110 250,150 110,150"
                fill="#e5e7eb"
                stroke="#9ca3af"
                strokeWidth="1"
              />

              {/* Side Face */}
              <polygon
                points="200,70 250,110 250,150 200,110"
                fill="#d1d5db"
                stroke="#9ca3af"
                strokeWidth="1"
              />

              {/* ---------- LENGTH ---------- */}
              {/* Guide lines */}
              <line
                x1="60"
                y1="70"
                x2="60"
                y2="50"
                stroke="#9ca3af"
                strokeDasharray="3 3"
              />
              <line
                x1="200"
                y1="70"
                x2="200"
                y2="50"
                stroke="#9ca3af"
                strokeDasharray="3 3"
              />

              {/* Dimension line */}
              <line
                x1="60"
                y1="50"
                x2="200"
                y2="50"
                stroke="#2563eb"
                strokeWidth="1.2"
                markerStart="url(#arrowBlue)"
                markerEnd="url(#arrowBlue)"
              />

              <text
                x="130"
                y="38"
                textAnchor="middle"
                fill="#2563eb"
                fontSize="12"
                fontWeight="500"
              >
                Length (L)
              </text>

              {/* ---------- WIDTH ---------- */}
              <line
                x1="200"
                y1="70"
                x2="260"
                y2="105"
                stroke="#9ca3af"
                strokeDasharray="3 3"
              />
              <line
                x1="250"
                y1="110"
                x2="290"
                y2="95"
                stroke="#9ca3af"
                strokeDasharray="3 3"
              />

              <line
                x1="260"
                y1="105"
                x2="290"
                y2="95"
                stroke="#16a34a"
                strokeWidth="1.2"
                markerStart="url(#arrowGreen)"
                markerEnd="url(#arrowGreen)"
              />

              <text x="285" y="85" fill="#16a34a" fontSize="12">
                Width (W)
              </text>

              {/* ---------- HEIGHT ---------- */}
              <line
                x1="250"
                y1="110"
                x2="270"
                y2="110"
                stroke="#9ca3af"
                strokeDasharray="3 3"
              />
              <line
                x1="250"
                y1="150"
                x2="270"
                y2="150"
                stroke="#9ca3af"
                strokeDasharray="3 3"
              />

              <line
                x1="270"
                y1="110"
                x2="270"
                y2="150"
                stroke="#dc2626"
                strokeWidth="1.2"
                markerStart="url(#arrowRed)"
                markerEnd="url(#arrowRed)"
              />

              <text
                x="285"
                y="130"
                fill="#dc2626"
                fontSize="12"
                transform="rotate(90 285,130)"
              >
                Height (H)
              </text>
            </svg>
          </div>

          {/* RESULT */}
          <div className="w-full">
            {result && (
              <div className="bg-gray-50 p-4 rounded-xl shadow-inner border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-800 mb-1">
                  Result
                </h3>

                <div className="space-y-1 text-gray-700 text-sm">
                  <div>
                    <p className="font-medium">
                      Volume:{" "}
                      <span className="font-semibold">
                        {result.volumeM3.toFixed(2)} Cubic meters
                      </span>
                    </p>
                    <p className="text-gray-500 ml-1">
                      {/* {result.volumeYd3.toFixed(2)} Cubic Yards |{" "} */}
                      {result.volumeFt3.toFixed(2)} Cubic Feet
                    </p>
                  </div>

                  <div className="pt-3 border-t">
                    <p className="text-gray-500 mb-1">
                      Using density: 2130 kg/m³
                      {/* (133 lbs/ft³) */}
                    </p>
                    <p>
                      <span className="font-medium">Weight:</span>{" "}
                      {result.weightKg.toFixed(2)} kg
                      {/* {result.weightLbs.toFixed(2)} lbs */}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {result && (
        <div className="mt-8">
          {/* Two Column Layout */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
            <div>
              <QuickChecklist
                checklist={checklist}
                setChecklist={setChecklist}
              />
              <ReadinessScore score={readinessScore} />
            </div>

            <div>
              <CalculatorNotes />
              {/* Buttons */}
              <div className="mt-10 sm:mt-16 flex flex-col items-center gap-4">
                {allowDownload ? (
                  <DownloadPDF
                    calculatorName={calculatorName}
                    result={result}
                    checklist={checklist}
                    score={readinessScore}
                    phone="+91 9124001120"
                    clearForm={clearForm}
                    autoDownload={allowDownload}
                    onAutoDownloadComplete={() => setAllowDownload(false)}
                  />
                ) : (
                  <button
                    className="px-16 sm:px-24 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700"
                    onClick={() => setShowInquiryModal(true)}
                  >
                    Generate PDF
                  </button>
                )}
                <a
                  href="tel:+919124001120"
                  className="bg-btnColor text-white px-12 sm:px-20 py-3 rounded-lg hover:bg-btnHoverColor transition"
                >
                  📞 Call for Enquiry
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
      {showInquiryModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg overflow-hidden w-full max-w-md relative">
            <button
              onClick={() => setShowInquiryModal(false)}
              className="absolute top-3 right-3 text-gray-500"
            >
              ✕
            </button>

            <CalculatorInquiryModal
              calculatorName={calculatorName}
              onSuccess={() => {
                setShowInquiryModal(false);
                setAllowDownload(true);
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default SlabCalculator;
